import { t } from '../../../../../../common/i18n.js'
import { Language } from '../../../../../../common/types/i18n.js'
import { UtensilsCrossed } from '../../../../../components/icon/solar-icons.js'
import type { OffboardFormData } from '../../../../../components/portal-offboard-modal/portal-offboard-modal.js'
import { GroupTitle } from '../../../../../components/section/section.js'
import type {
    CompensationData,
    CompensationLineItemGroup,
    CompensationLineItemInput,
    CompensationType,
    CustomFieldWithValue,
    EmergencyContact,
    EmployeeProfileData,
    EmploymentHistoryRecord,
    EmploymentRecordInput,
    EmploymentRefData,
    ExperienceEntry,
    ExperienceInput,
    LocationChangeData,
    RequestConfirmFn,
    WorkingPatternHistoryRecord,
} from '../employee-profile-types.js'
import '../employee-profile-view.css'
import { CompensationSection } from './compensation-section.js'
import { CustomFieldsSection } from './custom-fields-section.js'
import { EditableInfoSection } from './editable-info-section.js'
import { EmploymentSection } from './employment-section.js'
import { ExperiencesSection } from './experiences-section.js'
import { GeneralInfoSection } from './general-info-section.js'
import { HistorySection } from './history-section.js'
import { PersonalSection } from './personal-section.js'
import { SignatureTab } from './signature-tab.js'

interface DataTabProps {
    employee: EmployeeProfileData
    emergencyContacts: EmergencyContact[]
    compensationData: CompensationData | null
    compensationLineItems: CompensationLineItemGroup[] | null
    employmentHistory: EmploymentHistoryRecord[]
    workingPatterns: WorkingPatternHistoryRecord[]
    customFields: CustomFieldWithValue[]
    isAdmin: boolean
    showSignature: boolean
    lang: Language
    requestConfirm: RequestConfirmFn
    onCreateCompensation?: (data: {
        gross_amount: number
        currency: string
        pay_period: string
        fte: number
        effective_from: string
        note: string
    }) => Promise<boolean>
    onUpdateEmploymentDates?: (data: {
        hire_date?: string | null
        contract_start?: string | null
        probation_end_date?: string | null
        collaboration_type?: string | null
    }) => Promise<boolean>
    employmentRefData?: EmploymentRefData
    onCreateEmploymentRecord?: (data: EmploymentRecordInput) => Promise<boolean>
    locationChange?: LocationChangeData
    onRevealCompensation?: () => Promise<void>
    onUpdatePersonal?: (data: Record<string, string>) => Promise<boolean>
    canOffboardUsers?: boolean
    onChangeStatus?: (status: 'active' | 'inactive' | 'on_leave') => Promise<boolean>
    onOffboard?: (data: OffboardFormData) => Promise<boolean>
    onSaveCustomFields?: (values: Record<number, string | null>) => Promise<boolean>
    onAddEmergencyContact?: (data: {
        name: string
        relationship: string
        phone: string
    }) => Promise<boolean>
    onUpdateEmergencyContact?: (
        contactId: number,
        data: { name: string; relationship: string; phone: string },
    ) => Promise<boolean>
    onDeleteEmergencyContact?: (contactId: number) => Promise<boolean>
    experiences: ExperienceEntry[]
    onAddExperience?: (data: ExperienceInput) => Promise<boolean>
    onUpdateExperience?: (entryId: number, data: Omit<ExperienceInput, 'type'>) => Promise<boolean>
    onDeleteExperience?: (entryId: number) => Promise<boolean>
    onUpdateCompensation?: (
        compensationId: number,
        data: {
            gross_amount: number
            currency: string
            pay_period: string
            fte: number
            effective_from: string
            effective_to: string | null
            note: string
        },
    ) => Promise<boolean>
    onDeleteCompensation?: (compensationId: number) => Promise<boolean>
    compensationTypes?: CompensationType[]
    onCreateLineItem?: (data: CompensationLineItemInput) => Promise<boolean>
    onUpdateLineItem?: (itemId: number, data: CompensationLineItemInput) => Promise<boolean>
    onDeleteLineItem?: (itemId: number) => Promise<boolean>
    onDeleteEmploymentRecord?: (recordId: number) => Promise<boolean>
    onWorkingPatternsRefresh?: () => Promise<void>
}

/**
 * The "Andmed" tab, restructured for HR (TASK-438) into logical sections:
 * 1. Üldinfo — everyday work contact & organisational belonging
 * 2. Töösuhe ja -koormus — contract/employment info + working time (FTE),
 *    with the dynamic offboarding module
 * 3. Tasustamine ja boonused — salaries, additional compensation, bank details
 * 4. Isiklikud andmed — private-person data & emergency contacts
 * 5. Tervis ja toitumine — dietary preferences & allergies
 * 6. Lisaväljad + Ametikohtade ja töösuhte ajalugu (read-only, auto-generated)
 *
 * Visibility and edit rights stay permission-driven: the server resolves the
 * viewer's per-target capabilities (honouring the role's scope) and ships them
 * on `employee.viewer`; each section/row gates itself on those.
 */
export const DataTab = (props: DataTabProps) => {
    const ph = t.portal
    const { lang, isAdmin } = props
    const v = props.employee.viewer

    const canViewWorkData = v?.canViewWorkData ?? false
    const showCompensation = v?.canViewCompensation ?? false
    const canViewBank = v?.canViewPrivate ?? false

    return (
        <>
            <GeneralInfoSection
                employee={props.employee}
                lang={lang}
                onUpdatePersonal={props.onUpdatePersonal}
                employmentRefData={props.employmentRefData}
                onCreateEmploymentRecord={props.onCreateEmploymentRecord}
                locationChange={props.locationChange}
            />

            <EmploymentSection
                employee={props.employee}
                workingPatterns={props.workingPatterns}
                isAdmin={isAdmin}
                canViewWorkData={canViewWorkData}
                canEditWorkData={v?.canEditWorkData ?? false}
                showCompensation={showCompensation}
                lang={lang}
                onUpdateEmploymentDates={props.onUpdateEmploymentDates}
                canOffboardUsers={props.canOffboardUsers}
                onChangeStatus={props.onChangeStatus}
                onOffboard={props.onOffboard}
                onUpdatePersonal={props.onUpdatePersonal}
                onWorkingPatternsRefresh={props.onWorkingPatternsRefresh}
                requestConfirm={props.requestConfirm}
            />

            {(showCompensation || canViewBank) && (
                <CompensationSection
                    employee={props.employee}
                    compensationData={props.compensationData}
                    compensationLineItems={props.compensationLineItems}
                    showCompensation={showCompensation}
                    canViewBank={canViewBank}
                    canEditBank={v?.canEditPrivate ?? false}
                    lang={lang}
                    onCreateCompensation={props.onCreateCompensation}
                    onUpdateCompensation={props.onUpdateCompensation}
                    onDeleteCompensation={props.onDeleteCompensation}
                    compensationTypes={props.compensationTypes}
                    onCreateLineItem={props.onCreateLineItem}
                    onUpdateLineItem={props.onUpdateLineItem}
                    onDeleteLineItem={props.onDeleteLineItem}
                    onRevealCompensation={props.onRevealCompensation}
                    onUpdatePersonal={props.onUpdatePersonal}
                    requestConfirm={props.requestConfirm}
                />
            )}

            <PersonalSection
                employee={props.employee}
                emergencyContacts={props.emergencyContacts}
                lang={lang}
                onUpdatePersonal={props.onUpdatePersonal}
                onAddEmergencyContact={props.onAddEmergencyContact}
                onUpdateEmergencyContact={props.onUpdateEmergencyContact}
                onDeleteEmergencyContact={props.onDeleteEmergencyContact}
                requestConfirm={props.requestConfirm}
            />

            {/* Health & dietary — manager-tier per the profile spec */}
            {(v?.canViewHealth ?? false) && (
                <EditableInfoSection
                    icon={UtensilsCrossed}
                    title={ph.profile.healthDietarySection(lang)}
                    canEdit={v?.canEditHealth ?? false}
                    lang={lang}
                    onSave={props.onUpdatePersonal}
                    values={{
                        dietary_preference: props.employee.dietary_preference,
                        allergies: props.employee.allergies,
                        prefer_not_to_eat: props.employee.prefer_not_to_eat,
                    }}
                    fields={[
                        { key: 'dietary_preference', label: ph.profile.dietaryPreference(lang) },
                        { key: 'allergies', label: ph.profile.allergies(lang), multiline: true },
                        {
                            key: 'prefer_not_to_eat',
                            label: ph.profile.preferNotToEat(lang),
                            multiline: true,
                        },
                    ]}
                />
            )}

            {/* Lisaväljad — after Tervis ja toitumine, before the history (TASK-300) */}
            <CustomFieldsSection
                customFields={props.customFields}
                lang={lang}
                onSaveCustomFields={props.onSaveCustomFields}
            />

            {/* Kogemused — repeater entries (TASK-300), before the employment history */}
            {(v?.canViewExperiences ?? false) && (
                <ExperiencesSection
                    entries={props.experiences}
                    canEdit={v?.canEditExperiences ?? false}
                    lang={lang}
                    onAdd={props.onAddExperience}
                    onUpdate={props.onUpdateExperience}
                    onDelete={props.onDeleteExperience}
                    requestConfirm={props.requestConfirm}
                />
            )}

            {canViewWorkData && (
                <HistorySection
                    employee={props.employee}
                    employmentHistory={props.employmentHistory}
                    lang={lang}
                    onDeleteEmploymentRecord={props.onDeleteEmploymentRecord}
                    requestConfirm={props.requestConfirm}
                />
            )}

            {props.showSignature && (
                <>
                    <GroupTitle>{t.profile.signatureTab(lang)}</GroupTitle>
                    <SignatureTab employee={props.employee} lang={lang} />
                </>
            )}
        </>
    )
}
